/**
 * The Events page (Story 24, task 158; design r9 "Story 24 — events").
 *
 * A log viewer over the selected Client's normalized events: sign-ins and
 * directory audits today. The search lives in the URL (`lib/eventSearch`),
 * and a URL is a search: opening the page, following a link or pressing
 * Search with the URL changed starts one. The filter bar is a draft until
 * Search is pressed, the selects included, because every search is a paid
 * Athena scan and a filter that applied on change would scan per click.
 *
 * A search is asynchronous (ADR-0019 §6): start it, then poll its first
 * page every 1.5 s while it runs, for at most a minute. Its up-to-1,000
 * rows page for free after that. A search that was cut off by the scan
 * cap, or matched more than 1,000 rows, says so rather than showing a
 * silently partial list (requirements r23).
 *
 * Expanding a row is a point query for the full normalized event; the
 * raw provider record behind it is fetched only on click (task 67's
 * pattern). There is no All-Clients view: a cross-Client search would be
 * a scan per Client.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';

import {
  ApiError,
  getEvent,
  getEventSearchPage,
  getRawEvent,
  startEventSearch,
  type EventRow,
  type EventSearchPage,
} from '@bluesphere/api-client';
import { PillButton, colors } from '@bluesphere/ui-kit';

import { useAccessToken } from '../auth/useAccessToken';
import { useSelectedTenant } from '../auth/useSelectedTenant';
import {
  absoluteTime,
  AssetContextPanel,
  feedLabel,
} from '@bluesphere/mdr-alert-panel';
import { filterSelectClass } from '../components/SeverityFilter';
import {
  EmptyState,
  ErrorState,
  Loading,
  SelectClientPrompt,
  SourceBadge,
} from '../components/states';
import {
  DEFAULT_EVENT_FEEDS,
  DEFAULT_EVENT_RANGE,
  EVENT_CLASS_OPTIONS,
  EVENT_FEED_OPTIONS,
  EVENT_RANGE_OPTIONS,
  RESULT_LABELS,
  RETENTION_DAYS,
  classLabel,
  eventResult,
  eventTimeIso,
  isFiltered,
  parseEventsSearch,
  requestFor,
  utcDate,
  type EventRange,
  type EventResult,
  type EventsSearch,
} from '../lib/eventSearch';

export const EVENT_POLL_MS = 1500;
export const EVENT_POLL_LIMIT_MS = 60_000;

/**
 * Header and row share one template. On a phone the row is time,
 * operation and result; user and IP fold into a second line.
 */
const COLS =
  'grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[9.5rem_minmax(0,1.6fr)_minmax(0,1.2fr)_8rem_6.5rem] items-center gap-3';

const inputClass = `${filterSelectClass} placeholder:text-white/50`;

export function Events() {
  const { tenantId } = useSelectedTenant();
  const search = useSearch({ from: '/events' });

  if (!tenantId) return <SelectClientPrompt what="Events" />;

  return <EventsForClient tenantId={tenantId} search={search} />;
}

function EventsForClient({ tenantId, search }: { tenantId: string; search: EventsSearch }) {
  const idToken = useAccessToken();
  const navigate = useNavigate({ from: '/events' });
  // Pressing Search on an unchanged URL runs it again: the data may have moved.
  const [run, setRun] = useState(0);
  const [startedAt, setStartedAt] = useState(() => Date.now());

  const submit = (next: EventsSearch) => {
    setRun((n) => n + 1);
    setStartedAt(Date.now());
    void navigate({ search: next });
  };

  const start = useQuery({
    queryKey: ['event-search-start', tenantId, search, run],
    queryFn: () => startEventSearch(tenantId, requestFor(search), idToken),
    enabled: Boolean(idToken),
    staleTime: Infinity,
    gcTime: 5 * 60_000,
    retry: false,
  });
  const searchId = start.data?.search_id;

  // A new search restarts the poll's minute.
  useEffect(() => {
    if (start.isFetching) setStartedAt(Date.now());
  }, [start.isFetching]);

  const results = useInfiniteQuery({
    queryKey: ['event-search', tenantId, searchId],
    queryFn: ({ pageParam }) =>
      getEventSearchPage(tenantId, searchId as string, idToken, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: EventSearchPage) =>
      last.state === 'succeeded' ? last.next_cursor ?? undefined : undefined,
    enabled: Boolean(idToken && searchId),
    staleTime: Infinity,
    retry: false,
    refetchInterval: (query) => {
      const first = query.state.data?.pages[0];
      const running = !first || first.state === 'running';
      return running && Date.now() - startedAt < EVENT_POLL_LIMIT_MS ? EVENT_POLL_MS : false;
    },
  });

  const first = results.data?.pages[0];
  const running = start.isPending || results.isPending || first?.state === 'running';
  // Ticks while running, so the elapsed time shows and the poll's limit is noticed.
  const now = useNow(running);
  const gaveUp = first?.state === 'running' && now - startedAt >= EVENT_POLL_LIMIT_MS;
  const rows: EventRow[] =
    first?.state === 'succeeded' ? (results.data?.pages.flatMap((p) => p.items) ?? []) : [];
  const filtered = isFiltered(search);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Events</h1>
        <p className="text-sm text-white/70">
          Sign-ins and directory audits from Entra ID · newest first · kept {RETENTION_DAYS} days
        </p>
      </div>

      <FilterBar search={search} onSubmit={submit} busy={running && !gaveUp} />

      <div className="bs-card p-5">
        {start.isError ? (
          <ErrorState
            title="Could not start the search"
            error={start.error}
            onRetry={() => void start.refetch()}
          />
        ) : results.isError ? (
          <ErrorState
            title="Could not read the search"
            error={results.error}
            onRetry={() => void results.refetch()}
          />
        ) : gaveUp ? (
          <EmptyState
            title="Still searching"
            body="The search has run for over a minute. It may still finish; check again, or narrow the range."
            action={
              <PillButton
                variant="outline"
                size="sm"
                onClick={() => {
                  setStartedAt(Date.now());
                  void results.refetch();
                }}
              >
                Check again
              </PillButton>
            }
          />
        ) : running ? (
          <Running seconds={Math.max(0, Math.round((now - startedAt) / 1000))} />
        ) : first?.state === 'failed' ? (
          <FailedSearch reason={first.reason} onRetry={() => submit(search)} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={filtered ? 'No events match this search' : 'No events in the last 7 days'}
            body={
              filtered
                ? 'Nothing matches these filters in this range.'
                : 'Nothing has been received for this Client. Check that Entra ID sign-in & audit logs are enabled on the Integrations page.'
            }
            action={
              filtered ? (
                <PillButton variant="outline" size="sm" onClick={() => submit({})}>
                  Clear filters
                </PillButton>
              ) : undefined
            }
          />
        ) : (
          <>
            {first?.truncated && (
              <p
                className="mb-3 rounded-cardInner bg-bluesphere-tintElevated px-4 py-2 text-sm text-bluesphere-textOnLight"
                role="status"
              >
                Showing the newest 1,000 — narrow the range or add a filter to see the rest.
              </p>
            )}
            <div
              className={`hidden md:grid ${COLS} px-4 py-2 text-xs font-semibold uppercase tracking-wider text-bluesphere-muted`}
            >
              <div>Time</div>
              <div>Event</div>
              <div>User</div>
              <div>IP</div>
              <div className="text-right">Result</div>
            </div>
            <div className="space-y-1">
              {rows.map((row) => (
                <EventRowItem key={`${row.feed}:${row.dt}:${row.event_uid}`} tenantId={tenantId} row={row} />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-bluesphere-muted">
              <span>
                {rows.length.toLocaleString()} event{rows.length === 1 ? '' : 's'}
                {first && ` · scanned ${formatBytes(first.scanned_bytes)}`}
              </span>
              {results.hasNextPage && (
                <PillButton
                  variant="outline"
                  size="sm"
                  onClick={() => void results.fetchNextPage()}
                  disabled={results.isFetchingNextPage}
                >
                  {results.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </PillButton>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- the filter bar ----------

type Draft = {
  range: EventRange;
  from: string;
  to: string;
  feeds: string[];
  cls: string;
  user: string;
  device: string;
  ip: string;
  q: string;
};

function draftOf(search: EventsSearch): Draft {
  const today = utcDate(new Date());
  return {
    range: search.range ?? DEFAULT_EVENT_RANGE,
    from: search.from ?? utcDate(new Date(), -7),
    to: search.to ?? today,
    feeds: search.feeds ? search.feeds.split(',') : [...DEFAULT_EVENT_FEEDS],
    cls: search.class ? String(search.class) : '',
    user: search.user ?? '',
    device: search.device ?? '',
    ip: search.ip ?? '',
    q: search.q ?? '',
  };
}

function searchOf(d: Draft): EventsSearch {
  // The parser drops defaults and junk, so the URL carries only what the search means.
  return parseEventsSearch({
    range: d.range,
    from: d.from,
    to: d.to,
    feeds: d.feeds.join(','),
    class: d.cls,
    user: d.user,
    device: d.device,
    ip: d.ip,
    q: d.q,
  });
}

function FilterBar({
  search,
  onSubmit,
  busy,
}: {
  search: EventsSearch;
  onSubmit: (next: EventsSearch) => void;
  busy: boolean;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(search));
  // Back and Forward change the URL under the form; follow them.
  const key = JSON.stringify(search);
  useEffect(() => setDraft(draftOf(search)), [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const toggleFeed = (feed: string) =>
    set({
      feeds: draft.feeds.includes(feed)
        ? draft.feeds.filter((f) => f !== feed)
        : [...draft.feeds, feed],
    });

  const today = utcDate(new Date());
  const earliest = utcDate(new Date(), -RETENTION_DAYS);

  return (
    <form
      className="space-y-2"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSubmit(searchOf(draft));
      }}
      role="search"
      aria-label="Search events"
    >
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="search"
          aria-label="Free text"
          placeholder="Search text — an operation, app, error reason, target…"
          value={draft.q}
          maxLength={128}
          onChange={(e) => set({ q: e.target.value })}
          className={`${inputClass} sm:flex-1`}
        />
        <PillButton type="submit" variant="secondary" disabled={busy}>
          {busy ? 'Searching…' : 'Search'}
        </PillButton>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <select
          aria-label="Date range"
          value={draft.range}
          onChange={(e) => set({ range: e.target.value as EventRange })}
          className={filterSelectClass}
        >
          {EVENT_RANGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} className="text-bluesphere-textOnLight">
              {o.label}
            </option>
          ))}
        </select>
        {draft.range === 'custom' && (
          <div className="flex items-center gap-2 text-sm text-white/80">
            <input
              type="date"
              aria-label="From (UTC)"
              value={draft.from}
              min={earliest}
              max={today}
              onChange={(e) => set({ from: e.target.value })}
              className={`${inputClass} scheme-dark`}
            />
            <span>to</span>
            <input
              type="date"
              aria-label="To (UTC, inclusive)"
              value={draft.to}
              min={earliest}
              max={today}
              onChange={(e) => set({ to: e.target.value })}
              className={`${inputClass} scheme-dark`}
            />
          </div>
        )}
        <select
          aria-label="Event class"
          value={draft.cls}
          onChange={(e) => set({ cls: e.target.value })}
          className={filterSelectClass}
        >
          {EVENT_CLASS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} className="text-bluesphere-textOnLight">
              {o.label}
            </option>
          ))}
        </select>
        <fieldset className="flex items-center gap-3 text-sm text-white/90">
          <legend className="sr-only">Feeds</legend>
          {EVENT_FEED_OPTIONS.map((o) => (
            <label key={o.value} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={draft.feeds.includes(o.value)}
                onChange={() => toggleFeed(o.value)}
                className="h-4 w-4 accent-bluesphere-secondary"
              />
              {o.label}
            </label>
          ))}
        </fieldset>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <input
          type="text"
          aria-label="User"
          placeholder="User — UPN or object id"
          value={draft.user}
          maxLength={256}
          onChange={(e) => set({ user: e.target.value })}
          className={inputClass}
        />
        <input
          type="text"
          aria-label="Device"
          placeholder="Device — name or id"
          value={draft.device}
          maxLength={256}
          onChange={(e) => set({ device: e.target.value })}
          className={inputClass}
        />
        <input
          type="text"
          aria-label="IP address"
          placeholder="IP address"
          value={draft.ip}
          maxLength={64}
          onChange={(e) => set({ ip: e.target.value })}
          className={inputClass}
        />
      </div>
    </form>
  );
}

// ---------- states ----------

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

function Running({ seconds }: { seconds: number }) {
  return (
    <div className="flex flex-col items-center gap-2 py-12" role="status" aria-live="polite">
      <Loading height="h-12" />
      <p className="text-sm text-bluesphere-muted">
        Searching… {seconds > 0 ? `${seconds}s` : ''}
      </p>
    </div>
  );
}

function FailedSearch({
  reason,
  onRetry,
}: {
  reason: EventSearchPage['reason'];
  onRetry: () => void;
}) {
  if (reason === 'scan_cap') {
    return (
      <EmptyState
        title="This search would scan too much — narrow the range"
        body="It passed the per-search scan limit and was stopped, so no partial list is shown. Pick a shorter range or add a filter."
      />
    );
  }
  return (
    <EmptyState
      title={reason === 'timeout' ? 'The search timed out' : 'The search failed'}
      body="Nothing was returned. Try it again; if it keeps failing, narrow the range."
      action={
        <PillButton variant="outline" size="sm" onClick={onRetry}>
          Search again
        </PillButton>
      }
    />
  );
}

// ---------- rows ----------

const RESULT_COLORS: Record<EventResult, { color: string; tint: string }> = {
  success: { color: colors.success, tint: '#E5F8F3' },
  failure: { color: colors.danger, tint: colors.tintCritical },
  interrupted: { color: colors.warning, tint: colors.tintElevated },
  other: { color: colors.muted, tint: '#F1F2F4' },
  unknown: { color: colors.muted, tint: '#F1F2F4' },
};

function ResultChip({ result }: { result: EventResult }) {
  const { color, tint } = RESULT_COLORS[result];
  return (
    <span className="bs-chip" style={{ backgroundColor: tint, color: '#2A3241' }}>
      <span
        aria-hidden="true"
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: color }}
      />
      {RESULT_LABELS[result]}
    </span>
  );
}

function EventRowItem({ tenantId, row }: { tenantId: string; row: EventRow }) {
  const [open, setOpen] = useState(false);
  const iso = eventTimeIso(row.time);
  const result = eventResult(row.status_id, row.feed);
  const user = row.user_name ?? row.user_uid;

  return (
    <div className={open ? 'rounded-cardInner bg-bluesphere-surfaceMuted' : ''}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`bs-row grid w-full ${COLS} px-4 py-2.5 text-left`}
      >
        <div className="hidden text-xs tabular-nums text-bluesphere-textSecondary md:block" title={`${row.time} UTC`}>
          {preciseTime(iso)}
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold" title={row.operation}>
            {row.operation || classLabel(row.class_uid)}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <SourceBadge label={feedLabel(row.feed)} />
            <span className="text-xs text-bluesphere-muted">{classLabel(row.class_uid)}</span>
            {row.app_name && (
              <span className="min-w-0 truncate text-xs text-bluesphere-muted">· {row.app_name}</span>
            )}
          </div>
          {/* The desktop columns, as a meta line on a phone. */}
          <div className="mt-1 flex flex-wrap gap-x-2 text-xs text-bluesphere-muted md:hidden">
            <span className="tabular-nums" title={`${row.time} UTC`}>
              {preciseTime(iso)}
            </span>
            {user && <span className="min-w-0 truncate">· {user}</span>}
            {row.src_ip && <span className="font-mono">· {row.src_ip}</span>}
          </div>
        </div>
        <div className="hidden min-w-0 truncate text-sm text-bluesphere-textSecondary md:block" title={user ?? ''}>
          {user ?? '—'}
        </div>
        <div className="hidden truncate font-mono text-xs text-bluesphere-textSecondary md:block">
          {row.src_ip ?? '—'}
        </div>
        <div className="text-right">
          <ResultChip result={result} />
        </div>
      </button>
      {open && <EventDetail tenantId={tenantId} row={row} />}
    </div>
  );
}

function EventDetail({ tenantId, row }: { tenantId: string; row: EventRow }) {
  const idToken = useAccessToken();
  const event = useQuery({
    queryKey: ['event', tenantId, row.feed, row.dt, row.event_uid],
    queryFn: () => getEvent(tenantId, row.feed, row.dt, row.event_uid, idToken),
    enabled: Boolean(idToken),
    staleTime: Infinity,
    retry: false,
  });
  const timedOut = event.error instanceof ApiError && event.error.status === 504;

  return (
    <div className="px-4 pb-4 pt-1">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
        <Field label="Time (UTC)" value={row.time} mono />
        <Field label="Event id" value={row.event_uid} mono />
        {row.user_uid && <Field label="User object id" value={row.user_uid} mono />}
        {row.device_name && <Field label="Device" value={row.device_name} />}
        {row.device_uid && <Field label="Device id" value={row.device_uid} mono />}
      </dl>

      {/* The device's asset context (task 184): what NinjaOne and
          ConnectSecure know about the machine, matched by name as on the
          detection and alert pages. */}
      {row.device_name && (
        <div className="mt-3">
          <AssetContextPanel tenantId={tenantId} asset={row.device_name} />
        </div>
      )}

      {event.isPending && <Loading height="h-24" />}
      {event.isError && (
        <div className="mt-3">
          <ErrorState
            title={
              timedOut ? 'Looking up the event took too long' : 'Could not load the event'
            }
            error={event.error}
            onRetry={() => void event.refetch()}
          />
        </div>
      )}
      {event.data && (
        <>
          <div className="mt-3 text-xs font-semibold uppercase tracking-wider text-bluesphere-muted">
            Normalized event (OCSF)
          </div>
          <JsonBlock value={event.data.event} />
        </>
      )}

      <RawPayload tenantId={tenantId} row={row} />
    </div>
  );
}

/** The provider's own record (task 67's pattern): fetched on click only. */
function RawPayload({ tenantId, row }: { tenantId: string; row: EventRow }) {
  const idToken = useAccessToken();
  const [open, setOpen] = useState(false);
  const raw = useQuery({
    queryKey: ['event-raw', tenantId, row.feed, row.dt, row.event_uid],
    queryFn: () => getRawEvent(tenantId, row.feed, row.dt, row.event_uid, idToken),
    enabled: open && Boolean(idToken),
    staleTime: Infinity,
    retry: false,
  });
  const notRetained = raw.error instanceof ApiError && raw.error.status === 404;

  return (
    <div className="mt-3 border-t border-bluesphere-border pt-3">
      <PillButton variant="outline" size="sm" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {open ? 'Hide raw payload' : 'Show raw payload'}
      </PillButton>
      {open && raw.isPending && <Loading height="h-24" />}
      {open && raw.isError && notRetained && (
        <p className="mt-3 text-sm text-bluesphere-muted">
          The raw provider record is no longer retained for this event.
        </p>
      )}
      {open && raw.isError && !notRetained && (
        <div className="mt-3">
          <ErrorState
            title="Could not load the raw payload"
            error={raw.error}
            onRetry={() => void raw.refetch()}
          />
        </div>
      )}
      {open && raw.data && <JsonBlock value={raw.data.raw} />}
    </div>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex min-w-0 gap-2">
      <dt className="shrink-0 text-bluesphere-muted">{label}</dt>
      <dd className={`min-w-0 break-all text-bluesphere-textOnLight ${mono ? 'font-mono' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre className="bs-scroll mt-2 max-h-[420px] overflow-auto rounded-cardInner bg-white p-3 font-mono text-xs leading-relaxed text-bluesphere-textOnLight">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

/** To the second, in the viewer's zone: events are read in sequence, not as "3h ago". */
function preciseTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return absoluteTime(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}
